/**
 * Socket.IO Singleton Manager
 * Ensures only ONE socket instance exists for the entire app
 * Handles connection, disconnection, and room management
 */

import io from "socket.io-client";
import { getCookie } from "../utils/cookieHelper";

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";

let socketInstance = null;
let isConnecting = false;
const listeners = {}; // Track listeners to prevent duplicates
const joinedRooms = new Set(); // Track which rooms are joined
let currentUserId = null;
let listenerCounter = 0; // Counter for unique listener IDs
const debugLog = () => {};

/**
 * Initialize and return the singleton socket instance
 * Only connects once, subsequent calls return the existing instance
 */
export const initSocket = () => {
  if (socketInstance && socketInstance.connected) {
    debugLog("[Socket] Socket already connected");
    return socketInstance;
  }

  if (isConnecting) {
    debugLog("[Socket] Connection in progress, waiting...");
    return socketInstance;
  }

  try {
    isConnecting = true;
    debugLog("[Socket] Creating new socket connection");

    // Clear joined rooms when creating a new socket
    // This prevents stale state from old socket instance
    joinedRooms.clear();
    debugLog("[Socket] Cleared joined rooms state for new socket");

    socketInstance = io(SOCKET_URL, {
      autoConnect: false, // IMPORTANT: Don't connect until Chat page opens
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    setupSocketListeners();

    // IMPORTANT: Only connect if userId is already set
    // Otherwise, connect will happen in setUserId() or on Chat mount
    if (currentUserId) {
      debugLog("[Socket] UserId already available, connecting...");
      socketInstance.connect();
    } else {
      debugLog("[Socket] Waiting for userId before connecting");
    }
    isConnecting = false;
  } catch (error) {
    console.error("[Socket] Failed to create socket:", error);
    isConnecting = false;
  }

  return socketInstance;
};

/**
 * Set current user id (used for registerUser)
 * Stores userId and emits registerUser if socket is already connected
 * Initiates connection if socket exists but not connected yet
 */
export const setUserId = (userId) => {
  currentUserId = userId ? userId.toString() : null;

  debugLog("[Socket] setUserId called with:", currentUserId);

  if (!socketInstance) {
    debugLog(
      "[Socket] Socket not created yet, userId will be registered when socket connects",
    );
    return;
  }

  // If socket exists but not connected, connect now that we have userId
  if (!socketInstance.connected && currentUserId) {
    debugLog(
      "[Socket] Socket created but not connected, initiating connection with userId:",
      currentUserId,
    );
    socketInstance.connect();
    return;
  }

  // If already connected, emit registerUser immediately
  if (socketInstance && socketInstance.connected && currentUserId) {
    debugLog(
      "[Socket] Socket already connected, registering user:",
      currentUserId,
    );
    socketInstance.emit("registerUser", currentUserId);
  } else if (!socketInstance.connected) {
    debugLog("[Socket] Socket not connected yet, will register on connect");
  }
};

/**
 * Get the socket instance (doesn't create, just returns)
 */
export const getSocket = () => {
  return socketInstance;
};

/**
 * Setup core socket event listeners
 */
const setupSocketListeners = () => {
  if (!socketInstance) return;

  // Handle reconnection
  socketInstance.on("connect", () => {
    debugLog("[Socket] ✅ Connected successfully!");
    if (currentUserId && socketInstance && socketInstance.connected) {
      debugLog("[Socket] Registering user on connect:", currentUserId);
      socketInstance.emit("registerUser", currentUserId);
    }
    autoJoinFromCookie();
    rejoinAllRooms();
  });

  socketInstance.on("disconnect", (reason) => {
    debugLog("[Socket] ❌ Disconnected. Reason:", reason);
  });

  socketInstance.on("reconnect", (attemptNumber) => {
    debugLog("[Socket] 🔄 Reconnected after", attemptNumber, "attempts");
  });

  socketInstance.on("reconnect_attempt", (attemptNumber) => {
    debugLog("[Socket] 🔄 Attempting to reconnect... Attempt:", attemptNumber);
  });

  socketInstance.on("reconnect_error", (error) => {
    console.error("[Socket] ❌ Reconnection error:", error);
  });

  socketInstance.on("reconnect_failed", () => {
    console.error("[Socket] ❌ Reconnection failed completely");
  });

  socketInstance.on("error", (error) => {
    console.error("[Socket] ❌ Socket error:", error);
  });

  socketInstance.on("connect_error", (error) => {
    console.error("[Socket] ❌ Connection error:", error);
  });
};

/**
 * Auto-join active match room from cookie on connect
 */
const autoJoinFromCookie = () => {
  if (!socketInstance || !socketInstance.connected) return;

  const savedMatchId = getCookie("activeMatchId");
  if (!savedMatchId) return;

  const roomId = savedMatchId.toString();
  if (joinedRooms.has(roomId)) {
    debugLog(`[Socket] Already joined room from cookie: ${roomId}`);
    return;
  }

  debugLog(`[Socket] Joining room from cookie: ${roomId}`);
  socketInstance.emit("joinMatch", roomId);
  joinedRooms.add(roomId);
};

/**
 * Join a specific match room
 * Only joins if not already joined (idempotent)
 */
export const joinRoom = (matchId) => {
  if (!socketInstance) {
    console.warn("[Socket] Socket not initialized, cannot join room");
    return;
  }

  const roomId = matchId.toString();

  // Prevent duplicate joins
  if (joinedRooms.has(roomId)) {
    debugLog(`[Socket] Already joined room: ${roomId}`);
    return;
  }

  // Always track the room, even if not connected yet
  joinedRooms.add(roomId);

  if (!socketInstance.connected) {
    console.warn(
      `[Socket] Socket not connected yet, queued room join: ${roomId}`,
    );
    return;
  }

  debugLog(`[Socket] Joining room: ${roomId}`);
  socketInstance.emit("joinMatch", roomId);
};

/**
 * Leave a specific match room
 */
export const leaveRoom = (matchId) => {
  if (!socketInstance) return;

  const roomId = matchId.toString();

  if (!joinedRooms.has(roomId)) {
    debugLog(`[Socket] Not in room: ${roomId}`);
    return;
  }

  debugLog(`[Socket] Leaving room: ${roomId}`);
  socketInstance.emit("leaveMatch", roomId);
  joinedRooms.delete(roomId);
};

/**
 * Rejoin all previously joined rooms (called on reconnect)
 */
const rejoinAllRooms = () => {
  if (!socketInstance || !socketInstance.connected) return;

  debugLog(`[Socket] Rejoining ${joinedRooms.size} rooms`);

  joinedRooms.forEach((roomId) => {
    debugLog(`[Socket] Rejoining room: ${roomId}`);
    socketInstance.emit("joinMatch", roomId);
  });
};

/**
 * Register a listener for socket events
 * Allows multiple listeners for the same event (unlike previous implementation)
 */
export const on = (event, handler) => {
  if (!socketInstance) {
    console.warn("[Socket] Socket not initialized");
    return;
  }

  // Create a UNIQUE key for this listener instance using counter
  // This allows multiple handlers for the same event
  listenerCounter++;
  const listenerId = `${event}_${listenerCounter}`;

  // Add new listener and track it
  socketInstance.on(event, handler);
  listeners[listenerId] = { event, handler };

  debugLog(`[Socket] Listener registered: ${listenerId}`);

  // Return cleanup function
  return () => {
    if (!socketInstance) {
      debugLog(
        `[Socket] Cannot unregister ${listenerId} - socket not connected`,
      );
      return;
    }
    socketInstance.off(event, handler);
    delete listeners[listenerId];
    debugLog(`[Socket] Listener unregistered: ${listenerId}`);
  };
};

/**
 * Unregister a listener
 */
export const off = (event, handler) => {
  if (!socketInstance) return;
  socketInstance.off(event, handler);
};

/**
 * Emit a socket event
 */
export const emit = (event, data) => {
  if (!socketInstance || !socketInstance.connected) {
    console.warn(`[Socket] Cannot emit "${event}" - socket not connected`);
    return;
  }

  debugLog(`[Socket] Emitting: ${event}`, data);
  socketInstance.emit(event, data);
};

/**
 * Disconnect socket (only on logout)
 */
export const disconnectSocket = () => {
  if (socketInstance) {
    debugLog("[Socket] Disconnecting socket");
    joinedRooms.clear();
    socketInstance.disconnect();
    socketInstance = null;
    isConnecting = false;
  }
};

/**
 * Check if socket is connected
 */
export const isConnected = () => {
  return socketInstance && socketInstance.connected;
};

/**
 * Get list of joined rooms (for debugging)
 */
export const getJoinedRooms = () => {
  return Array.from(joinedRooms);
};

const socketService = {
  initSocket,
  getSocket,
  setUserId,
  joinRoom,
  leaveRoom,
  on,
  off,
  emit,
  disconnectSocket,
  isConnected,
  getJoinedRooms,
};

export default socketService;
